import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  eInvoiceInvoiceDataAction,
  eInvoiceGenerateAction,
} from "../../../redux/actions/sales-action";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import LoadingSpinner from "../../helper-components/loading-spinner";
import { ChevronDown, ChevronUp } from "lucide-react";

/* ─── Dropdown styling ──────────────────────────────────────────────────────── */
const SELECT_CLASS =
  "border border-[var(--input-border)] rounded-[6px] px-3 bg-[var(--input-bg)] text-[var(--input-color)] font-[var(--input-font-weight)] focus:outline-none focus:ring-1 focus:ring-[var(--button-bg-color)]";

/* ─── shared layout constants (matches e-way bill) ──────────────────────────── */
const LABEL_CLASS =
  "text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] whitespace-nowrap";

/* ─── Read-only field item (vertical layout for grids) ──────────────────────── */
export const FieldItem = ({ label, value, singleLine = false }) => (
  <div className="flex flex-col gap-[4px] min-w-0">
    <span className={`${LABEL_CLASS} truncate`} title={label}>
      {label}
    </span>
    <span
      className="border border-[var(--input-border)] rounded-[6px] px-3 py-[7px] bg-[var(--input-bg)] text-[var(--input-color)] font-[var(--input-font-weight)]"
      style={{ minHeight: "36px", display: "flex", alignItems: "center", fontSize: "13px", lineHeight: "1.45", overflowWrap: "anywhere" }}
      title={singleLine && value ? String(value) : undefined}
    >
      {value == null ? (
        <span className="text-[var(--placeholder-color)]">—</span>
      ) : singleLine ? (
        <span className="truncate min-w-0">{value}</span>
      ) : (
        value
      )}
    </span>
  </div>
);

/* ─── Select dropdown field item ────────────────────────────────────────────── */
const SelectField = ({ label, value, options, onChange, name }) => (
  <div className="flex flex-col gap-[4px] relative min-w-0">
    <span className={`${LABEL_CLASS} truncate`} title={label}>{label}</span>
    <div className="relative w-full">
      <select
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        className={`${SELECT_CLASS} w-full appearance-none pr-8 cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed`}
        style={{ minHeight: "36px", fontSize: "13px" }}
      >
        <option value="" disabled>Select {label}</option>
        {options?.map((opt, i) => (
          <option key={i} value={opt?.value ?? opt?.code ?? opt}>
            {opt?.name ?? opt?.value ?? opt}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
        </svg>
      </div>
    </div>
  </div>
);

/* ─── Collapsible section ─────────────────────────────────────────────────────  */
export const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border border-[var(--light-border-color)] rounded-[10px] overflow-hidden mb-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 bg-[var(--create-table-header-bg)] text-white"
      >
        <span className="font-[600] uppercase tracking-wide" style={{ fontSize: "12px" }}>
          {title}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="px-5 py-4 flex flex-col gap-0">{children}</div>}
    </section>
  );
};

const YES_NO_OPTIONS = [
  { value: "N", name: "No" },
  { value: "Y", name: "Yes" },
];

/* Address blocks are sent with Addr1 only (Addr2 is not part of the expected payload) */
const withoutAddr2 = (dtls) => {
  if (!dtls) return dtls;
  const { Addr2, ...rest } = dtls;
  return rest;
};

/* Generate request body – the prepared payload in the expected shape, no authToken */
const buildIrpPayload = (p) => ({
  monthYear: p?.monthYear,
  Version: p?.Version,
  soit_id: p?.soit_id,
  TranDtls: p?.TranDtls,
  DocDtls: p?.DocDtls,
  SellerDtls: withoutAddr2(p?.SellerDtls),
  BuyerDtls: withoutAddr2(p?.BuyerDtls),
  ...(p?.DispDtls ? { DispDtls: withoutAddr2(p.DispDtls) } : {}),
  ...(p?.ShipDtls ? { ShipDtls: withoutAddr2(p.ShipDtls) } : {}),
  ItemList: p?.ItemList,
  ValDtls: p?.ValDtls,
});

/* NIC duplicate-IRN error code: an IRN already exists on the IRP for this document */
const NIC_DUPLICATE_IRN = "2150";

/* Locate the NIC/IRP response block (ErrorResponse, IRN, AckNo…) inside the API error body */
const findNicResponse = (obj, depth = 0) => {
  if (!obj || typeof obj !== "object" || depth > 4) return null;
  if ("ErrorResponse" in obj || "ErrorDetails" in obj) return obj;
  for (const value of Object.values(obj)) {
    const found = findNicResponse(value, depth + 1);
    if (found) return found;
  }
  return null;
};

/* Turn a failed generate response into { message, details[], duplicate } for display */
export const parseIrpError = (err) => {
  const body = err?.data ?? err ?? {};
  const nic = findNicResponse(body) ?? {};
  const rawList = nic.ErrorResponse ?? nic.ErrorDetails ?? [];
  const errorList = Array.isArray(rawList) ? rawList : [rawList];

  const details = errorList
    .map((e) => {
      if (typeof e === "string") return e;
      const text = e?.ErrorInfo ?? e?.ErrorMessage ?? e?.message;
      if (!text) return null;
      const code = e?.ErrorCode && !String(text).startsWith(String(e.ErrorCode)) ? `${e.ErrorCode} : ` : "";
      const column = e?.ColumnName
        ? ` (${e.ColumnName}${e?.ColumnValue != null ? ` = ${e.ColumnValue}` : ""})`
        : "";
      return `${code}${text}${column}`;
    })
    .filter(Boolean);

  const isDuplicate = errorList.some(
    (e) =>
      String(e?.ErrorCode ?? "") === NIC_DUPLICATE_IRN ||
      String(e?.ErrorInfo ?? e?.ErrorMessage ?? e ?? "").trim().startsWith(NIC_DUPLICATE_IRN)
  );

  return {
    message: body?.message ?? err?.message ?? "Failed to generate e-Invoice",
    details,
    duplicate: isDuplicate
      ? { irn: nic.IRN ?? nic.Irn ?? null, ackNo: nic.AckNo ?? null, ackDate: nic.AckDate ?? nic.AckDt ?? null }
      : null,
  };
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const CreateEInvoice = ({ invoiceId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  /* NIC IRN payload prepared by the backend (response.data) – edited in place */
  const [payload, setPayload] = useState(null);
  const [masters, setMasters] = useState({});
  const [validation, setValidation] = useState({ valid: true, errors: [], warnings: [] });
  const [eInvoiceStatus, setEInvoiceStatus] = useState(null);
  const [pushError, setPushError] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const tryDone = () => setLoading(false);

    /* ── Prepared e-Invoice payload (GET) ─────────────────────────────────── */
    if (invoiceId && invoiceId !== "new") {
      dispatch(
        eInvoiceInvoiceDataAction(
          { soit_id: invoiceId },
          (res) => {
            const prepared = res?.data ?? null;
            /* Place of supply defaults to the seller's state code */
            setPayload(
              prepared?.SellerDtls?.Stcd
                ? { ...prepared, BuyerDtls: { ...prepared.BuyerDtls, Pos: prepared.SellerDtls.Stcd } }
                : prepared
            );
            setMasters(res?.EInvoiceMastersData ?? {});
            setValidation({
              valid: res?.validation?.valid ?? true,
              errors: res?.validation?.errors ?? [],
              warnings: res?.validation?.warnings ?? [],
            });
            setEInvoiceStatus(res?.eInvoiceStatus ?? null);
            tryDone();
          },
          (err) => {
            errorToast(err?.data?.message ?? err?.message ?? "Failed to fetch invoice data for e-Invoice");
            tryDone();
          }
        )
      );
    } else {
      tryDone();
    }
  }, [invoiceId]);

  if (loading) return <LoadingSpinner />;

  const tranDtls = payload?.TranDtls ?? {};
  const docDtls = payload?.DocDtls ?? {};
  const sellerDtls = payload?.SellerDtls ?? {};
  const buyerDtls = payload?.BuyerDtls ?? {};
  const dispDtls = payload?.DispDtls ?? null;
  const shipDtls = payload?.ShipDtls ?? null;
  const itemList = payload?.ItemList ?? [];
  const valDtls = payload?.ValDtls ?? {};

  const stateLabel = (code) => {
    if (!code) return null;
    const state = masters?.states?.find((s) => s?.code === String(code));
    return state ? `${code} – ${state.name}` : String(code);
  };

  /* name is "<Section>.<Field>", e.g. "DocDtls.Typ" */
  const handleDropdownChange = (name, value) => {
    const [section, field] = name.split(".");
    setPayload((prev) => ({
      ...prev,
      [section]: { ...prev?.[section], [field]: value },
    }));
  };

  const handleItemChange = (idx, field, value) => {
    setPayload((prev) => ({
      ...prev,
      ItemList: (prev?.ItemList ?? []).map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      ),
    }));
  };

  const canGenerate = eInvoiceStatus?.can_generate !== false;
  const hasErrors = (validation?.errors?.length ?? 0) > 0;
  const pushDisabled = !payload || !canGenerate || hasErrors || submitting || !!pushError?.duplicate;

  const handlePushToIRP = () => {
    if (pushDisabled) return;

    setSubmitting(true);
    setPushError(null);
    dispatch(
      eInvoiceGenerateAction(
        buildIrpPayload(payload),
        (res) => {
          setSubmitting(false);
          successToast(res?.message ?? "e-Invoice Generated Successfully");
          navigate("/sales-invoice");
        },
        (err) => {
          setSubmitting(false);
          const parsed = parseIrpError(err);
          setPushError(parsed);
          errorToast(parsed.details[0] ?? parsed.message);
          bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        }
      )
    );
  };

  const renderMessage = (msg, i) => (
    <li key={i}>
      {msg?.field ? <span className="font-[600]">{msg.field}: </span> : null}
      {msg?.message ?? String(msg)}
    </li>
  );

  return (
    <div className="flex flex-col h-full">
      {/* ════ SCROLLABLE BODY ══════════════════════════════════════════════ */}
      <div ref={bodyRef} className="flex-1 overflow-y-auto px-6 py-5">

        {/* ── IRP PUSH ERROR ───────────────────────────────────────────────── */}
        {pushError && (
          <div className="mb-4 rounded-[8px] border border-[#FECDCA] bg-[#FEF3F2] px-4 py-3 text-[#B42318]" style={{ fontSize: "13px" }}>
            <div className="font-[600] mb-1">{pushError.message}</div>
            {pushError.details.length > 0 && (
              <ul className="list-disc pl-5">
                {pushError.details.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            )}
            {pushError.duplicate && (
              <div className="mt-2">
                <div>
                  The IRP already has an IRN for this invoice, but it has not been recorded in this system.
                  Do not push again – the existing IRN needs to be synced to this invoice.
                </div>
                {pushError.duplicate.irn && (
                  <div className="mt-1" style={{ overflowWrap: "anywhere" }}>
                    <span className="font-[600]">Existing IRN:</span> {pushError.duplicate.irn}
                  </div>
                )}
                {pushError.duplicate.ackNo && (
                  <div>
                    <span className="font-[600]">Ack No:</span> {pushError.duplicate.ackNo}
                    {pushError.duplicate.ackDate ? ` (${pushError.duplicate.ackDate})` : ""}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── STATUS / VALIDATION BANNERS ──────────────────────────────────── */}
        {eInvoiceStatus && !canGenerate && (
          <div className="mb-4 rounded-[8px] border border-[#B2DDFF] bg-[#EFF8FF] px-4 py-3 text-[#175CD3]" style={{ fontSize: "13px" }}>
            <div className="font-[600]">e-Invoice status: {eInvoiceStatus.status ?? "—"}</div>
            {eInvoiceStatus.irn && <div>IRN: {eInvoiceStatus.irn}</div>}
            {eInvoiceStatus.ack_no && (
              <div>Ack No: {eInvoiceStatus.ack_no}{eInvoiceStatus.ack_date ? ` (${eInvoiceStatus.ack_date})` : ""}</div>
            )}
            {eInvoiceStatus.blocked_reason && <div>{eInvoiceStatus.blocked_reason}</div>}
          </div>
        )}
        {hasErrors && (
          <div className="mb-4 rounded-[8px] border border-[#FECDCA] bg-[#FEF3F2] px-4 py-3 text-[#B42318]" style={{ fontSize: "13px" }}>
            <div className="font-[600] mb-1">Fix these before pushing to IRP</div>
            <ul className="list-disc pl-5">{validation.errors.map(renderMessage)}</ul>
          </div>
        )}
        {validation?.warnings?.length > 0 && (
          <div className="mb-4 rounded-[8px] border border-[#FEDF89] bg-[#FFFAEB] px-4 py-3 text-[#B54708]" style={{ fontSize: "13px" }}>
            <div className="font-[600] mb-1">Please confirm</div>
            <ul className="list-disc pl-5">{validation.warnings.map(renderMessage)}</ul>
          </div>
        )}

        {/* ── TRANSACTION DETAILS (TOP) ────────────────────────────────────── */}
        <Section title="Transaction Details">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SelectField
              label="Document Type Code"
              name="DocDtls.Typ"
              value={docDtls?.Typ}
              options={masters?.docType ?? []}
              onChange={handleDropdownChange}
            />
            <FieldItem label="Document Number" value={docDtls?.No} />
            <FieldItem label="Document Date" value={docDtls?.Dt} />
            <FieldItem label="Total Invoice Value" value={valDtls?.TotInvVal} />
            <SelectField
              label="Supply Type Code"
              name="TranDtls.SupTyp"
              value={tranDtls?.SupTyp}
              options={masters?.supplyType ?? []}
              onChange={handleDropdownChange}
            />
            <SelectField
              label="Reverse Charge"
              name="TranDtls.RegRev"
              value={tranDtls?.RegRev}
              options={masters?.reverseCharge ?? YES_NO_OPTIONS}
              onChange={handleDropdownChange}
            />
            <SelectField
              label="IGST on Intra-State"
              name="TranDtls.IgstOnIntra"
              value={tranDtls?.IgstOnIntra}
              options={YES_NO_OPTIONS}
              onChange={handleDropdownChange}
            />
            <FieldItem
              label="IRN – Invoice Reference Number"
              value={eInvoiceStatus?.irn}
              singleLine
            />
          </div>
        </Section>

        {/* ── SELLER & BUYER DETAILS (GRID) ──────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="Organisation (Seller) Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldItem label="Supplier Legal Name" value={sellerDtls?.LglNm} />
              <FieldItem label="Supplier GSTIN" value={sellerDtls?.Gstin} />
              <FieldItem label="Supplier Address" value={sellerDtls?.Addr1} />
              <FieldItem label="Supplier Place" value={sellerDtls?.Loc} />
              <FieldItem label="Supplier State Code" value={stateLabel(sellerDtls?.Stcd)} />
              <FieldItem label="Supplier Pincode" value={sellerDtls?.Pin} />
              {dispDtls && (
                <>
                  <FieldItem label="Dispatch From – Name" value={dispDtls?.Nm} />
                  <FieldItem label="Dispatch From – Address" value={dispDtls?.Addr1} />
                  <FieldItem label="Dispatch From – Place" value={dispDtls?.Loc} />
                  <FieldItem label="Dispatch From – Pincode" value={dispDtls?.Pin} />
                  <FieldItem label="Dispatch From – State Code" value={stateLabel(dispDtls?.Stcd)} />
                </>
              )}
            </div>
          </Section>

          <Section title="Customer (Buyer) Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldItem label="Recipient Legal Name" value={buyerDtls?.LglNm} />
              <FieldItem label="Recipient GSTIN" value={buyerDtls?.Gstin} />
              <FieldItem label="Recipient Address" value={buyerDtls?.Addr1} />
              <FieldItem label="Recipient Place" value={buyerDtls?.Loc} />
              <FieldItem label="Recipient State Code" value={stateLabel(buyerDtls?.Stcd)} />
              <FieldItem label="Recipient Pincode" value={buyerDtls?.Pin} />
              <SelectField
                label="Place of Supply State Code"
                name="BuyerDtls.Pos"
                value={buyerDtls?.Pos}
                options={(masters?.states ?? []).map((s) => ({ value: s.code, name: `${s.code} – ${s.name}` }))}
                onChange={handleDropdownChange}
              />
              {shipDtls && (
                <>
                  <FieldItem label="ShippingTo Legal Name" value={shipDtls?.LglNm} />
                  <FieldItem label="ShippingTo GSTIN" value={shipDtls?.Gstin} />
                  <FieldItem label="ShippingTo Address" value={shipDtls?.Addr1} />
                  <FieldItem label="ShippingTo Place" value={shipDtls?.Loc} />
                  <FieldItem label="ShippingTo State Code" value={stateLabel(shipDtls?.Stcd)} />
                  <FieldItem label="ShippingTo Pincode" value={shipDtls?.Pin} />
                </>
              )}
            </div>
          </Section>
        </div>

        {/* ── ITEM DETAILS ─────────────────────────────────────────────────── */}
        <section className="border border-[var(--light-border-color)] rounded-[10px] overflow-hidden mb-4">
          <div className="px-5 py-3 bg-[var(--create-table-header-bg)] text-white">
            <span className="font-[600] uppercase tracking-wide" style={{ fontSize: "12px" }}>
              Item Details
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ fontSize: "13px" }}>
              <thead>
                <tr className="bg-[var(--list-table-heading-bg)] text-[var(--list-table-heading-fg-color)]">
                  {["#", "Item Name", "HSN Code", "Qty", "Unit", "Unit Price", "Total Amount", "Discount", "Taxable Value", "GST Rate", "IGST", "CGST", "SGST", "Item Total", "Is Service"].map((h) => (
                    <th key={h} className="px-4 py-[10px] text-left font-[500] border-b border-[var(--light-border-color)] whitespace-nowrap" style={{ fontSize: "12px" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itemList.length > 0 ? (
                  itemList.map((item, idx) => (
                    <tr key={item?.SlNo ?? idx} className="border-b border-[var(--light-border-color)] hover:bg-[var(--row-hover-color)]">
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.SlNo ?? idx + 1}</td>
                      <td className="px-4 py-3 text-[var(--secondary)] font-[500]">{item?.PrdDesc ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.HsnCd ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.Qty ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        <select
                          className={`${SELECT_CLASS} w-[90px] cursor-pointer`}
                          style={{ minHeight: "30px", fontSize: "12px", padding: "0 8px" }}
                          value={item?.Unit ?? ""}
                          onChange={(e) => handleItemChange(idx, "Unit", e.target.value)}
                        >
                          {(masters?.units ?? []).map((u) => (
                            <option key={u.code} value={u.code}>{u.code}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.UnitPrice ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.TotAmt ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.Discount ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.AssAmt ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.GstRt ?? "—"}%</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.IgstAmt ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.CgstAmt ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.SgstAmt ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">{item?.TotItemVal ?? "—"}</td>
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        <select
                          className={`${SELECT_CLASS} w-[70px] cursor-pointer`}
                          style={{ minHeight: "30px", fontSize: "12px", padding: "0 8px" }}
                          value={item?.IsServc ?? "N"}
                          onChange={(e) => handleItemChange(idx, "IsServc", e.target.value)}
                        >
                          <option value="N">No</option>
                          <option value="Y">Yes</option>
                        </select>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={15} className="px-4 py-4 text-center text-[var(--placeholder-color)]">
                      No item data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── VALUE DETAILS ────────────────────────────────────────────────── */}
        <Section title="Value Details">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <FieldItem label="Taxable Value" value={valDtls?.AssVal} />
            <FieldItem label="IGST" value={valDtls?.IgstVal} />
            <FieldItem label="CGST" value={valDtls?.CgstVal} />
            <FieldItem label="SGST" value={valDtls?.SgstVal} />
            <FieldItem label="Round Off" value={valDtls?.RndOffAmt} />
            <FieldItem label="Total Invoice Value" value={valDtls?.TotInvVal} />
          </div>
        </Section>
      </div>

      {/* ════ STICKY FOOTER ═════════════════════════════════════════════════ */}
      <div
        className="border-t border-[var(--create-table-footer-border)] bg-white h-[4rem] flex justify-end items-center px-5 gap-3"
        style={{ boxShadow: "0 -2px 8px rgba(0,0,0,0.04)" }}
      >
        <button
          type="button"
          onClick={() => navigate("/sales-invoice")}
          className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
            bg-[var(--draft-button-bg)] cursor-pointer
            text-[var(--draft-button-fg)]
            border border-[var(--draft-button-border)]
            text-[length:var(--draft-button-fs)]
            font-[var(--draft-button-fw)]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handlePushToIRP}
          disabled={pushDisabled}
           className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
              bg-[var(--primary-button-bg-color)] cursor-pointer
              text-[var(--primary-button-fg-color)]
              border border-[var(--primary-button-bg-color)]
              hover:bg-[var(--primary-button-hover-color)]
              text-[length:var(--primary-button-create-fs)]
              font-[var(--primary-button-font-weight)]
              disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Pushing..." : "Push to IRP"}
        </button>
      </div>
    </div>
  );
};

export default CreateEInvoice;
